
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';

import { useProgrammation } from '../../Hooks/UseProgrammation';
import useTranslation from '../../Hooks/UseTranslation';
import { useProject } from '../../Hooks/UseProject';

import { deleteGroup, deleteTask } from '../../Services/ProgrammationManagerService';
import { deleteGroupAction, deleteTaskAction } from '../../Redux/Actions/ProgrammationManagerActions';

import Comments from './Sections/Comments/Comments';
import Constraints from './Sections/Constraints/Constraints';
import Costs from './Sections/Costs/Costs';
import Documents from './Sections/Documents/Documents';
import GroupInfo from './GroupInfo';
import Icon from '../Icon/Icon';
import Navigator from './components/Navigator';
import OptionsDialog from '../OptionsDialog/OptionsDialog';
import Relations from './Sections/Relations/Relations';
import Responsibles from './Sections/Responsibles/Responsibles';
import TaskGeneralInfo from './TaskGeneralInfo';
import WorksConstraints from './Sections/Constraints/WorksConstraints';
import WorksResponsibles from './WorksResponsibles';

import Dots from '../SvgImages/Dots';
import Times from '../SvgImages/Times';
import Spinner from '../SvgImages/Spinner/Spinner';

import './ProgrammationManager.scss';

interface Props {
  taskList: any[];
}

const ProgrammationManager = (props: Props) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { taskList } = props;

  const { project_id } = useProject()

  const { itemOpened, mode, editingItem, closeItem, setEditingItem, setError, clearErrors } = useProgrammation()

  const [ show, setShow ] = useState(false)

  const [ showUserDialog, setShowUserDialog ] = useState(false)

  const deleteItem = async () => {

    setEditingItem(true)

    let error
    let data

    if (itemOpened.type === 'group') {

      const res = await deleteGroup(itemOpened._id, project_id)

      error = res[0]
      data = res[1]

      if (!error) {
        dispatch(deleteGroupAction(data.taskList, data.deleted_tasks_ids))
      }
    } else {

      const res = await deleteTask(itemOpened._id)

      error = res[0]
      data = res[1]

      if (!error) {
        dispatch(deleteTaskAction(itemOpened._id, data.parentTask, data.taskList))
      }
    }

    setEditingItem(false)

    closeItem()

    if (error) {
      return console.log(error)
    }
  }

  const handleDelete = async () => {

    const alertTitle = itemOpened.type === 'group' ? t('PROGRAMMATION_MANAGER:DeleteGroupAlertTitle') : t('PROGRAMMATION_MANAGER:DeleteTaskAlertTitle')
    const alertContent = itemOpened.type === 'group' ? t('PROGRAMMATION_MANAGER:DeleteGroupAlertContent') : t('PROGRAMMATION_MANAGER:DeleteTaskAlertContent')

    const result = await Swal.fire({
      title: alertTitle,
      text: alertContent,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: t('PROGRAMMATION_MANAGER:DeleteAlertCancelButton'),
      confirmButtonText: t('PROGRAMMATION_MANAGER:DeleteAlertConfirmButton'),
      width: '500px',
    })

    if (!result.isConfirmed) {

      return
    }

    deleteItem()
  }

  const dialogOptions = useMemo(() => {

    const options: any[] = [
      {
        label: t('PROGRAMMATION_MANAGER:DeleteButton'),
        icon: <Icon name="trash" fas fw />,
        color: 'var(--color-danger)',
        onClick: handleDelete,
      }
    ]

    return options
  }, [ itemOpened ])

  const handleExit = async () => {

    clearErrors()

    if (mode === 'create') {

      if (itemOpened.name === '') {

        await deleteItem()
        return
      }
    } else {

      if (itemOpened.name === '') {

        setError({
          field: 'name',
          error: 'El nombre no puede estar vacío'
        })

        Swal.fire({
          title: 'El nombre no puede estar vacío',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          width: '500px',
        })
        return
      }
    }

    setShow(false)

    setTimeout(() => closeItem(), 300)
  }

  useEffect(() => {

    setShow(!!itemOpened)
  }, [itemOpened])

  return (
    <Fragment>
      <Modal
        show={show}
        dialogClassName={`programmation-manager-modal ${itemOpened?.parentId || itemOpened?.groupId ? 'with-parent' : ''}`}
        onHide={() => handleExit()}
        keyboard={!editingItem}
        {...(editingItem ? { backdrop: 'static' } : {})}
      >
        {itemOpened && (
          <Fragment>
            <Modal.Header>
              <Navigator
                parentId={itemOpened.parentId || itemOpened.groupId}
                parentName={itemOpened.parentName || itemOpened.groupName}
                parentPosition={itemOpened.parentPosition}
                parentType={itemOpened.parentType}
                itemId={itemOpened._id}
                itemName={itemOpened.name}
                itemPosition={itemOpened.position}
                itemType={itemOpened.type}
              />
              <button
                className="modal-options-button"
                onClick={() => setShowUserDialog(true)}
                disabled={editingItem}
              >
                <Dots
                  height={14}
                  width={14}
                />
              </button>
              <button
                className="modal-options-button"
                onClick={handleExit}
                disabled={editingItem}
              >
                {editingItem && (
                  <Spinner
                    height={24}
                    width={24}
                  />
                )}
                {!editingItem && (
                  <Times
                    height={24}
                    width={24}
                  />
                )}
              </button>
            </Modal.Header>
            <Modal.Body>
              <div className="programmation-manager-body-scrollable scroller">
                <Row>
                  <Col lg={8}>
                    <div className='programmation-manager-desktop-scrollable scroller'>
                      {itemOpened.type !== 'group' && (
                        <Fragment>
                          <TaskGeneralInfo taskList={taskList} />
                          {(itemOpened.type !== 'group' && !itemOpened.hasSubTasks) && (
                            <Constraints />
                          )}
                          <Costs />
                          {itemOpened.hasSubTasks && <WorksConstraints />}
                        </Fragment>
                      )}
                      {itemOpened.type === 'group' && (
                        <GroupInfo taskList={taskList} />
                      )}
                      {itemOpened.type !== 'group' && <Comments taskId={itemOpened._id} items={itemOpened.comments} />}
                    </div>
                  </Col>
                  <Col lg={4}>
                    <div className='programmation-manager-desktop-scrollable scroller'>
                      {itemOpened.type !== 'group' && <Relations />}
                      {(itemOpened.type !== 'group' && !itemOpened.hasSubTasks) && (
                        <Responsibles />
                      )}
                      {itemOpened.hasSubTasks && <WorksResponsibles />}
                      {itemOpened.type !== 'group' && <Documents />}
                    </div>
                  </Col>
                </Row>
              </div>
            </Modal.Body>
          </Fragment>
        )}
      </Modal>


      <OptionsDialog
          show={showUserDialog}
          title={'Opciones'}
          closeButton
          onHide={() => setShowUserDialog(false)}
          options={dialogOptions}
      />

    </Fragment>
  );
};

export default ProgrammationManager;
